/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `blood_pressure` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class BloodPressureMesg : Mesg {
    public constructor() : super("BloodPressure", Profile.MesgNum.BLOOD_PRESSURE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** systolic_pressure, in mmHg. */
    public var systolicPressure: UShort?
        get() = getFieldValue(SYSTOLIC_PRESSURE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SYSTOLIC_PRESSURE_FIELD_NUM, value)

    /** diastolic_pressure, in mmHg. */
    public var diastolicPressure: UShort?
        get() = getFieldValue(DIASTOLIC_PRESSURE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(DIASTOLIC_PRESSURE_FIELD_NUM, value)

    /** mean_arterial_pressure, in mmHg. */
    public var meanArterialPressure: UShort?
        get() = getFieldValue(MEAN_ARTERIAL_PRESSURE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MEAN_ARTERIAL_PRESSURE_FIELD_NUM, value)

    /** map_3_sample_mean, in mmHg. */
    public var map3SampleMean: UShort?
        get() = getFieldValue(MAP_3_SAMPLE_MEAN_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAP_3_SAMPLE_MEAN_FIELD_NUM, value)

    /** map_morning_values, in mmHg. */
    public var mapMorningValues: UShort?
        get() = getFieldValue(MAP_MORNING_VALUES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAP_MORNING_VALUES_FIELD_NUM, value)

    /** map_evening_values, in mmHg. */
    public var mapEveningValues: UShort?
        get() = getFieldValue(MAP_EVENING_VALUES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAP_EVENING_VALUES_FIELD_NUM, value)

    /** heart_rate, in bpm. */
    public var heartRate: UByte?
        get() = getFieldValue(HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(HEART_RATE_FIELD_NUM, value)

    /** heart_rate_type. */
    public var heartRateType: HrType?
        get() = (getFieldValue(HEART_RATE_TYPE_FIELD_NUM) as? UByte)?.let { HrType.fromValue(it) }
        set(value) = setFieldValue(HEART_RATE_TYPE_FIELD_NUM, value?.value)

    /** status. */
    public var status: BpStatus?
        get() = (getFieldValue(STATUS_FIELD_NUM) as? UByte)?.let { BpStatus.fromValue(it) }
        set(value) = setFieldValue(STATUS_FIELD_NUM, value?.value)

    /** user_profile_index. Associates this blood pressure message to a user. This corresponds to the index of the user profile message in the blood pressure file. */
    public var userProfileIndex: UShort?
        get() = getFieldValue(USER_PROFILE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(USER_PROFILE_INDEX_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val SYSTOLIC_PRESSURE_FIELD_NUM: UByte = 0u
        public const val DIASTOLIC_PRESSURE_FIELD_NUM: UByte = 1u
        public const val MEAN_ARTERIAL_PRESSURE_FIELD_NUM: UByte = 2u
        public const val MAP_3_SAMPLE_MEAN_FIELD_NUM: UByte = 3u
        public const val MAP_MORNING_VALUES_FIELD_NUM: UByte = 4u
        public const val MAP_EVENING_VALUES_FIELD_NUM: UByte = 5u
        public const val HEART_RATE_FIELD_NUM: UByte = 6u
        public const val HEART_RATE_TYPE_FIELD_NUM: UByte = 7u
        public const val STATUS_FIELD_NUM: UByte = 8u
        public const val USER_PROFILE_INDEX_FIELD_NUM: UByte = 9u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("SystolicPressure", 0u, BaseType.UINT16, 1.0, 0.0, "mmHg", false)
            fields[1] = Field("DiastolicPressure", 1u, BaseType.UINT16, 1.0, 0.0, "mmHg", false)
            fields[2] = Field("MeanArterialPressure", 2u, BaseType.UINT16, 1.0, 0.0, "mmHg", false)
            fields[3] = Field("Map3SampleMean", 3u, BaseType.UINT16, 1.0, 0.0, "mmHg", false)
            fields[4] = Field("MapMorningValues", 4u, BaseType.UINT16, 1.0, 0.0, "mmHg", false)
            fields[5] = Field("MapEveningValues", 5u, BaseType.UINT16, 1.0, 0.0, "mmHg", false)
            fields[6] = Field("HeartRate", 6u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[7] = Field("HeartRateType", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("Status", 8u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[9] = Field("UserProfileIndex", 9u, BaseType.UINT16, 1.0, 0.0, "", false)
            return fields
        }
    }
}
